<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const { windowWidth } = storeToRefs(useWindowSizeStore())
const footerIllustrationUrl = 'https://padlet.net/landing-pages/home/v1/footer.webp'
const illustrationHeight = 156
const illustrationWidth = 2211
const illustrationContainer = ref<HTMLDivElement>()

// We use maxAvailableWidth to handle the case when users zoom in/out which causes
// the windowWidth to change
const maxAvailableWidth = computed((): number => {
  if (!illustrationContainer.value) return windowWidth.value
  return Math.max(illustrationContainer.value.clientWidth, windowWidth.value)
})

const numberOfIllustrationsDisplayed = computed((): number => {
  if (maxAvailableWidth.value < illustrationWidth) return 1
  return Math.ceil(maxAvailableWidth.value / illustrationWidth)
})

const lastIllustrationWidth = computed((): number => {
  const remainingWidth = maxAvailableWidth.value - (numberOfIllustrationsDisplayed.value - 1) * illustrationWidth
  return remainingWidth <= 0 ? illustrationWidth : remainingWidth
})

function handleObjectPosition(shouldReverse: boolean): string {
  if (shouldReverse) {
    // For rtl, we need to position the object from the left
    if (dir() === 'ltr') return 'right'
    return 'left'
  }
  if (dir() === 'ltr') return 'left'
  return 'right'
}
</script>

<template>
  <div ref="illustrationContainer" :class="['flex h-[156px] bg-[#C3E776]']">
    <template v-if="illustrationContainer">
      <ImageThumbnail
        v-for="index in numberOfIllustrationsDisplayed"
        :key="index"
        :style="
          index < numberOfIllustrationsDisplayed
            ? { width: `${illustrationWidth}px` }
            : { width: `${lastIllustrationWidth}px` }
        "
        data-testid="footerIllustrationImage"
        :src="footerIllustrationUrl"
        :original-image-height="illustrationHeight"
        :original-image-width="illustrationWidth"
        :width="illustrationWidth"
        :height="illustrationHeight"
        resize-strategy="fit"
        :process-image="true"
        :object-position="handleObjectPosition(index % 2 === 0)"
        :flip-direction="index % 2 === 0 ? 'horizontal' : undefined"
        :lazy-loading="false"
        aria-hidden="true"
      />
    </template>
  </div>
</template>
